<template>
  <section class="datasets-page">
    <header class="datasets-page__header">
      <h1 class="datasets-page__heading">My Datasets</h1>
      <BaseLink :href="getPageSpecificUploadLink()">
        Upload New Dataset
      </BaseLink>
    </header>
    <section class="datasets-page__body">
      <header class="datasets-page__search-header">
        <BaseInput
          placeholder="Search"
          v-model="searchTerm"
          class="datasets-page__search-input"
        />
        <BaseSelectDropdown
          v-model="selectedDivisions"
          id="report-title-select"
          :options="reportTitleOptions"
          placeholder="Filter Datasets"
          multiselect
          tags
          left-aligned
        />
        <div class="datasets-page__search-button-group">
          <BaseButton title="Click to search">SEARCH</BaseButton>
          <BaseButton
            color="dark-grey"
            text-color="white"
            title="Click to clear search"
            @click="clearSearchParameters()"
          >
            CLEAR
          </BaseButton>
        </div>
      </header>
      <div class="datasets-page__content">
        <DatasetTable
          :auth-user="authUser"
          :data="actionRequiredTable"
          title="Action Required"
          :processingDatasets="datasetsForAnalysis"
          :deleteingDatasets="datasetsForDeletion"
          :subscriptions="subscriptions"
          @refresh-datasets="refreshDataset"
          @updated-company-profile="refreshDataset"
          @deleted-dataset="waitForDeletion"
          @failed-to-deleted-dataset="removeFailedDeleteDataset"
          @analized-dataset="waitForAnalysis"
          @deleted-dataset-successfully="handleSuccessfulDeleteDataset"
        />
        <DatasetTable
          :auth-user="authUser"
          :data="activeTable"
          title="Active"
          :processingDatasets="datasetsForAnalysis"
          :deleteingDatasets="datasetsForDeletion"
          :subscriptions="subscriptions"
          :favorites="favorites"
          @refresh-datasets="refreshDataset"
          @deleted-dataset="waitForDeletion"
          @failed-to-deleted-dataset="removeFailedDeleteDataset"
          @analized-dataset="waitForAnalysis"
          @failed-to-analize-dataset="removeFailedAnalysisDataset"
          @deleted-dataset-successfully="handleSuccessfulDeleteDataset"
        />
        <DatasetTable
          :auth-user="authUser"
          :data="pendingTable"
          title="Pending ITR Approval"
          :processingDatasets="datasetsForAnalysis"
          :deleteingDatasets="datasetsForDeletion"
          :subscriptions="subscriptions"
          @refresh-datasets="refreshDataset"
          @deleted-dataset="waitForDeletion"
          @failed-to-deleted-dataset="removeFailedDeleteDataset"
          @analized-dataset="waitForAnalysis"
          @deleted-dataset-successfully="handleSuccessfulDeleteDataset"
        />
        <DatasetTable
          :auth-user="authUser"
          :data="archivedTable"
          title="Archived"
          :processingDatasets="datasetsForAnalysis"
          :deleteingDatasets="datasetsForDeletion"
          :subscriptions="subscriptions"
          @refresh-datasets="refreshDataset"
          @deleted-dataset="waitForDeletion"
          @failed-to-deleted-dataset="removeFailedDeleteDataset"
          @analized-dataset="waitForAnalysis"
          @deleted-dataset-successfully="handleSuccessfulDeleteDataset"
        />
      </div>
    </section>
    <BaseDialog ref="confirmation-dialog" />
  </section>
</template>

<script>
  import DatasetTable from '~/components/dataset/DatasetTable.vue';
  import BaseLink from '~/components/base/BaseLink.vue';
  import BaseButton from '~/components/base/BaseButton.vue';
  import BaseDialog from '~/components/base/BaseDialog.vue';
  import BaseInput from '~/components/base/BaseInput.vue';
  import BaseSelectDropdown from '~/components/base/BaseSelectDropdown.vue';

  export default {
    name: 'MyDatasetsPage',
    components: {
      BaseLink,
      DatasetTable,
      BaseButton,
      BaseDialog,
      BaseInput,
      BaseSelectDropdown,
    },
    props: {
      // We have this `feed` on page load
      authUser: {
        type: Object,
        required: true,
      },
      feed: {
        type: Array,
        required: false,
        default: () => [],
      },
      users: {
        type: Object,
        required: false,
        default: () => [],
      },
      user: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      favorites: {
        type: Object,
        required: false,
        default: () => ({}),
      },
    },
    data: () => ({
      searchTerm: '',
      selectedDivisions: [],
      runAnalysisList: [],
      generatedFeed: [],
      datasetsForDeletion: [],
      datasetsForAnalysis: [],
      subscriptions: [],
    }),
    computed: {
      isRunningDataRefresh() {
        return (
          (this.pendingTable && this.pendingTable.length > 0) ||
          (this.datasetsForAnalysis && this.datasetsForAnalysis.length > 0)
        );
      },
      displayFeed() {
        return this.generatedFeed.filter((row) => {
          if (!this.searchTerm && this.selectedDivisions.length <= 0)
            return true;

          const uppercaseName = row.Name.toUpperCase();
          const uppercaseSearchTerm = this.searchTerm.toUpperCase();

          if (this.searchTerm && this.selectedDivisions.length > 0) {
            if (this.selectedDivisions.includes('')) {
              return (
                (this.selectedDivisions.includes(uppercaseName) &&
                  row.UsedIn === 'DataCast') ||
                (this.selectedDivisions.includes(uppercaseName) &&
                  uppercaseName.includes(uppercaseSearchTerm))
              );
            }

            return (
              this.selectedDivisions.includes(uppercaseName) &&
              uppercaseName.includes(uppercaseSearchTerm)
            );
          } else if (this.selectedDivisions.length > 0) {
            if (this.selectedDivisions.includes('')) {
              return (
                row.UsedIn === 'DataCast' ||
                this.selectedDivisions.includes(uppercaseName)
              );
            }
            return this.selectedDivisions.includes(uppercaseName);
          } else if (uppercaseSearchTerm) {
            return uppercaseName.includes(uppercaseSearchTerm);
          }

          return true;
        });
      },
      actionRequiredTable() {
        return this.displayFeed.filter((row) => {
          if (row.DatasetArchived) return false;
          return (
            (row.NewDataIsRequired && !row.PendingITRResponse) ||
            row.DataProfileRequired ||
            row.CompanyProfileRequired
          );
        });
      },
      activeTable() {
        return this.displayFeed.filter((row) => {
          if (row.CompanyProfileRequired) return false;
          if (row.DataProfileRequired) return false;
          if (row.PendingITRResponse) return false;
          if (row.DatasetArchived) return false;
          if (row.NewDataIsRequired) return false;
          return true;
        });
      },
      pendingTable() {
        return this.displayFeed.filter((row) => {
          if (row.DataProfileRequired) return false;
          if (row.DatasetArchived) return false;
          return row.PendingITRResponse;
        });
      },
      archivedTable() {
        return this.displayFeed.filter((row) => {
          if (row.DatasetArchived) return true;
        });
      },
      reportTitleOptions() {
        const reportTitles = [];

        if (!this.generatedFeed || this.generatedFeed.length <= 0) return;

        this.generatedFeed.forEach((dataset) => {
          let reportTitle = dataset.Name.toUpperCase();

          if (dataset.IsPortalData) {
            reportTitle = '';
          }

          reportTitles.push(reportTitle);

          // also grab all analysis that are running
          if (dataset.AnalysisStatus.IsRunning) {
            this.runAnalysisList.push({
              customer_dataset_id: dataset.ID,
              analysis_status: dataset.AnalysisStatus,
              isRunning: true,
            });
          }
        });

        const uniqueReportTitles = reportTitles.filter(
          (v, i, a) => a.indexOf(v) === i
        );
        uniqueReportTitles.sort();

        return uniqueReportTitles.map((title) => {
          return {
            value: title,
            label: title,
          };
        });
      },
    },
    watch: {
      isRunningDataRefresh(value) {
        if (value) {
          this.startIntervalToCheckDataStatus();
        }
      },
      generatedFeed(value) {
        if (this.datasetsForAnalysis && this.datasetsForAnalysis.length > 0) {
          this.datasetsForAnalysis.forEach((analysisId) => {
            const dataset = value.find(
              (row) => row.AnalysisStatus.ID === analysisId
            );
            if (dataset.AnalysisStatus.IsComplete) {
              this.datasetsForAnalysis = this.datasetsForAnalysis.filter(
                (id) => id !== analysisId
              );
            }
          });
        }
      },
    },
    async created() {
      this.generatedFeed = this.feed;
      this.datasetsForAnalysis = this.feed
        .filter((row) => row.AnalysisStatus.IsRunning)
        .map((row) => row.AnalysisStatus.ID);
      if (this.authUser.subscriptions || this.authUser.active_free_trials) {
        this.subscriptions = this.authUser.subscriptions
          .concat(this.authUser.active_free_trials)
          .map((subscription) => {
            return subscription.active ? subscription.product.name : '';
          });
      }
    },
    async mounted() {
      document.addEventListener('keydown', this.handleKeypress);

      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);

      if (urlParams.has('edit')) {
        await this.$refs['confirmation-dialog'].prompt({
          title: 'Edit Successful',
          message: 'Your dataset has been edited successfully.',
          confirmText: 'Ok',
          level: 'success',
        });
      }

      if (urlParams.has('upload')) {
        let message = '';
        if (urlParams.get('replace' === 'true')) {
          message = `Your dataset has been replaced successfully. You can find it in the "${urlParams.get('dataset_type') === 'datacast' ? 'Active' : 'Action Required'}" section.`;
        } else {
          message = `Your dataset has been uploaded successfully. You can find it in the "${urlParams.get('dataset_type') === 'datacast' ? 'Active' : 'Action Required'}" section.`;
        }
        await this.$refs['confirmation-dialog'].prompt({
          title: 'Upload Successful',
          message: message,
          confirmText: 'Ok',
          level: 'success',
        });
      }

      // Update the url to remove the query string
      history.pushState({}, '', window.location.pathname);
    },
    beforeUnmount() {
      document.removeEventListener('keydown', this.handleKeypress);
    },
    methods: {
      handleKeypress(event) {
        if (event.key === 'Escape' || event.key === 'Esc') {
          this.clearSearchParameters();
        }
      },
      async refreshDataset() {
        try {
          const { data } = await axios.get('/dashboard/dataset');
          this.generatedFeed = data.company_data;
        } catch (err) {
          // TODO: HANDLE ERROR - This is just silencing the error
        }
      },
      waitForDeletion(datasetId) {
        this.datasetsForDeletion.push(datasetId);
        this.refreshDataset();
      },
      handleSuccessfulDeleteDataset(datasetId) {
        this.datasetsForDeletion = this.datasetsForDeletion.filter(
          (id) => id !== datasetId
        );
        this.refreshDataset();
      },
      removeFailedDeleteDataset(datasetId) {
        this.datasetsForDeletion = this.datasetsForDeletion.filter(
          (id) => id !== datasetId
        );
        this.refreshDataset();
      },
      waitForAnalysis(analysisId) {
        this.datasetsForAnalysis.push(analysisId);
        this.refreshDataset();
      },
      removeFailedAnalysisDataset(analysisId) {
        this.datasetsForAnalysis = this.datasetsForAnalysis.filter(
          (id) => id !== analysisId
        );
        this.refreshDataset();
      },
      clearSearchParameters() {
        this.searchTerm = '';
        this.selectedDivisions = [];
      },
      startIntervalToCheckDataStatus() {
        setInterval(() => {
          if (!this.isRunningDataRefresh) {
            clearInterval();
            return;
          }
          this.refreshDataset();
        }, 60000);
      },
      getPageSpecificUploadLink() {
        // Check if the url is either dashboard, datacast or alliance
        const url = window.location.href;
        let location = 'dashboard';

        if (url.includes('/datacast/datasets')) {
          location = 'datacast';
        } else if (url.includes('/alliance/datasets')) {
          location = 'alliance';
        }

        return `/${location}/datasets/upload`;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .division-options {
    display: flex;
    justify-content: center;
    width: 100%;
  }
  .division-input {
    padding: 2px;
  }
  .division-title {
    flex: 1 auto;
  }

  header {
    display: flex;
    flex-wrap: wrap;
    z-index: 0;
    border-top: none;
  }
  .datasets-page {
    padding: 30px 20px;
    min-height: calc(
      100vh - (135px + 182.25px)
    ); // 135 is the height of the header, 182.25 is the height of the footer

    &__header {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      width: 100%;
      padding: 0 0 1rem 0;

      @include screen-md {
        flex-direction: row;
      }
    }
    &__heading {
      flex: 1 auto;
      margin: 0;
      margin-bottom: 4rem;
    }

    &__search-header {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
      margin-bottom: 2rem;

      @include screen-md {
        flex-direction: row;
        flex-wrap: nowrap;
        align-items: center;
      }
    }

    &__search-input {
      height: fit-content;
    }

    &__search-filters {
      width: 100%;
    }

    &__search-button-group {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 4px;
      width: 100%;

      @include screen-md {
        justify-content: flex-start;
      }
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin-top: 1rem;
    }
  }
  @media (min-width: 480px) {
    .datasets-page {
      padding: 40px 30px;
    }
  }
  @media (min-width: 800px) {
    .datasets-page {
      padding: 50px 40px;
    }
  }
  @media (min-width: 1000px) {
    .datasets-page {
      padding: 60px;
    }
    header h1 {
      margin-bottom: 40px;
    }
  }
  .wrapper-container {
    max-height: 70vh;
    overflow-y: scroll;
  }
</style>
